import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Dropdown as DropdownNs, Switcher } from '@typings';
import React from 'react';
import { mergeRefs } from 'react-laag';
import { PlacementType } from 'react-laag/dist/PlacementType';

import { Button } from '../Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../Dropdown';
import { TextEllipsis } from '../TextEllipsis';
import { Tooltip } from '../Tooltip';

type Props<T> = {
  tab: Switcher.Tab<T>;
  groupName: string;
  isSelected: boolean;
  onChange: (value: T) => void;
  onNestedOptionChange?: (value: T) => void;
  selectedTabOption?: string;
  tooltipPlacement?: PlacementType;
  isDisabled?: boolean;
  variant: Switcher.Variant;
  shouldShowTitleInDropdown?: boolean;
};

export const ContentSwitcherTab = React.forwardRef<HTMLButtonElement, Props<string | undefined>>((props, ref) => {
  const {
    tab,
    isSelected,
    onChange,
    tooltipPlacement = 'bottom-center',
    isDisabled,
    selectedTabOption = '',
    variant,
    shouldShowTitleInDropdown = true,
  } = props;
  const { value, title, icon: Icon, className, dataTestId, options, dropdownTitle } = tab;
  const hasIcon = isDefined(Icon);

  const hasOptions = React.useMemo(() => isDefined(options) && !isEmpty(options), [options]);

  const handleSelectionChange = ([selectedValue]: string[]) => {
    onChange(selectedValue);
  };
  const buttonTapCommonStyles = 'w-full h-full gap-0 px-2 transition-colors text-neutral-60';

  if (hasOptions && isSelected) {
    return (
      <Dropdown shouldShowTitleInDropdown={shouldShowTitleInDropdown} title={dropdownTitle}>
        <DropdownTrigger>
          {({ isOpen, triggerProps: { ref: triggerRef, ...restTriggerProps } }) => (
            <Button
              ref={mergeRefs(ref, triggerRef)}
              color="secondary"
              variant="text"
              isChevronOpen={isOpen}
              role="tab"
              data-testid={dataTestId}
              data-variant={variant}
              className={cn(
                buttonTapCommonStyles,
                'text-neutral-20 shadow-4 data-[variant=gray]:bg-neutral-96 data-[variant=light]:bg-white',
                {
                  'px-1.5': hasIcon,
                },
                className,
              )}
              {...restTriggerProps}
            >
              <Tooltip content={title} placement={tooltipPlacement} isDisabled={isOpen} disableOnTouchScreen>
                <span className="flex items-center">
                  {hasIcon && <Icon className={cn('size-5 text-neutral-60', { 'text-neutral-20': isSelected })} />}
                  {isDefined(title) && <TextEllipsis className="sr-only">{title}</TextEllipsis>}
                </span>
              </Tooltip>
            </Button>
          )}
        </DropdownTrigger>
        <DropdownMenu
          selectionMode="single"
          selectedKeys={[selectedTabOption]}
          items={options as DropdownNs.MenuItem[]}
          onSelectionChange={handleSelectionChange}
        />
      </Dropdown>
    );
  }

  const handleClick = () => onChange(value);

  return (
    <Tooltip content={hasIcon && title} placement={tooltipPlacement} disableOnTouchScreen>
      <span>
        <Button
          ref={ref}
          role="tab"
          color="secondary"
          variant="text"
          data-variant={variant}
          className={cn(
            buttonTapCommonStyles,
            {
              'text-neutral-20 shadow-4 data-[variant=gray]:bg-neutral-96 data-[variant=light]:bg-white': isSelected,
              'w-8 px-1.5': hasIcon,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              'w-12': hasIcon && hasOptions,
            },
            className,
          )}
          data-testid={dataTestId}
          onClick={handleClick}
          disabled={isDisabled}
          aria-selected={isSelected}
          icon={Icon}
          iconClassName={cn('size-5 text-neutral-60', { 'text-neutral-20': isSelected })}
          isChevronOpen={hasOptions ? false : undefined}
          tabIndex={isSelected ? 0 : -1}
        >
          {isDefined(title) && <span className={cn({ 'sr-only': hasIcon })}>{title}</span>}
        </Button>
      </span>
    </Tooltip>
  );
});
